import { format, parseISO } from 'date-fns';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Border, Breakpoints, Color, Grid, Type } from '../../../app/StyleGuide';
import { MediumButton, MediumPrimaryButton } from '../../../components/Buttons';
import ErrorMessage from '../../../components/ErrorMessage';
import { ModalBody, ModalContainer, ModalFooter, ModalHeader } from '../../../components/ModalElements';
import { createCurriculum } from '../../../hooks/useCurriculum';
import cacheService from '../../../services/cacheService';
import windowService from '../../../services/windowService';
import { logger } from '../../../utils/logger';
import { formatCurrency } from '../../../utils/textUtils';
import { useUser } from '../../authentication';
import {
  BillingPeriod,
  BrandCode,
  CurriculumBrand,
  CurriculumPricingEntity,
  PlanType,
  Subscription,
} from '../../curriculum';
import { changeSubscription } from '../api/changeSubscription';
import { pollForLicenseWithPreviousSubscription } from '../utils/pollForLicenseWithPreviousSubscription';
import { CurrentSubscriptionData } from '../SwitchCurriculum';
import CurriculumCoverImage from './CurriculumCoverImage';

const ModalContent = styled(ModalBody)`
  margin: ${Grid._3};
`;

const BibleStudyBrandDetails = styled.div<{ $backgroundImage?: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  max-width: 100%;
  height: 240px;
  text-align: center;
  gap: ${Grid._2};
  position: relative;
  border-radius: ${Border.radius};
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: ${Grid._3};

  &:before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    height: 40%;
    background: ${props => props.$backgroundImage || 'transparent'};
    background-size: cover;
    background-position: top center;
    background-repeat: no-repeat;
    filter: blur(28px) saturate(110%);
    transform: scale(1.1);
    z-index: 0;
    pointer-events: none;

    /* Fade out the bleed towards the middle for a smooth cutoff */
    -webkit-mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0));
    mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0));
  }

  > * {
    position: relative;
    z-index: 1;
  }

  h3 {
    margin: 0;
    font-size: 20px;
    font-weight: bold;
  }
  p {
    margin: 0;
    font-size: 12px;
    opacity: 0.7;
  }
`;

const SubscriptionContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const SubcriptionDetails = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  p {
    margin: 0;

    &:first-child {
      font-size: ${Type.Scale._4};
    }
  }
`;

const SubscriptionPrice = styled.div`
  font-size: 76px;
  font-weight: ${Type.Weight.semibold};
  color: ${Type.Color.medium};

  &:before {
    content: '$';
    font-size: 28px;
    position: relative;
    top: -34px;
  }

  @media (max-width: ${Breakpoints.screen_md}) {
    font-size: ${Type.Scale._8};

    &:before {
      font-size: ${Type.Scale._4};
      top: -10px;
    }
  }
`;

const ConfirmButton = styled(MediumPrimaryButton)`
  min-width: 180px;
`;

const AlertBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  background: ${Color.Secondary._05};
  border-radius: ${Border.radius};
  box-shadow: inset 0 0 0 1px rgba(81, 144, 197, 0.15);
  margin-top: ${Grid._5};
  padding: ${Grid._4};
  color: ${Color.Gray._70};

  p {
    margin: 0;
  }

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    padding: ${Grid._4};
  }
`;

const CompareCurriculumImage = styled(CurriculumCoverImage)`
  width: 100px;
  border-radius: ${Border.radius};
`;

const CompareCurriculumWrapper = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: ${Grid._4};
  align-items: center;
  gap: ${Grid._3};
  justify-content: space-between;
`;

const CurrentBibleStudyBrandDetails = styled(BibleStudyBrandDetails)`
  transform: scale(0.9);
  filter: grayscale(100%);

  @media (max-width: ${Breakpoints.screen_md}) {
    display: none;
  }
`;

const ChevronIcon = styled.i`
  color: ${Color.Gray._50};
  margin-right: ${Grid._3};

  @media (max-width: ${Breakpoints.screen_md}) {
    display: none;
  }
`;

export function ConfirmSubscriptionChangesModal({
  currentSubscriptionData,
  selectedCurriculum,
  selectedSubscription,
  selectedPlan,
  selectedBillingPeriod,
  handleDismiss,
}: {
  currentSubscriptionData?: CurrentSubscriptionData;
  selectedCurriculum?: { brand: CurriculumBrand; ageGroup: CurriculumPricingEntity };
  selectedSubscription?: Subscription;
  selectedPlan?: PlanType;
  selectedBillingPeriod?: BillingPeriod;
  handleDismiss: () => void;
}) {
  const { user } = useUser();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<boolean>(false);

  const confirm = () => {
    if (
      !currentSubscriptionData?.license?.subscription?.id ||
      !currentSubscriptionData?.subscription?.itemNumber ||
      !selectedSubscription?.itemNumber ||
      !user?.lastSelectedAccount ||
      !user?.userId
    ) {
      setError(true);
      return;
    }
    const subscriptionId = currentSubscriptionData.license.subscription.id;
    const orgId = user.lastSelectedAccount;

    setIsSubmitting(true);
    changeSubscription(
      subscriptionId,
      selectedSubscription.itemNumber,
      orgId,
      currentSubscriptionData?.subscription?.itemNumber,
      user.userId
    )
      .then(() => {
        cacheService.set('subscriptionChanged', {
          oldPlan: currentSubscriptionData?.subscription?.itemNumber,
          newPlan: selectedSubscription.itemNumber,
          renewalDate: currentSubscriptionData?.license?.endDate,
        });

        return pollForLicenseWithPreviousSubscription(orgId, subscriptionId);
      })
      .then(() => {
        handleDismiss();
        windowService.redirectTo('/org/purchases-and-subscriptions');
      })
      .catch(error => {
        setError(true);
        logger.error(error);
      })
      .finally(() => setIsSubmitting(false));
  };
  const curriculumBranding = createCurriculum(currentSubscriptionData?.brand?.code as BrandCode)?.brand;

  useEffect(() => {
    if (!currentSubscriptionData || !selectedCurriculum) {
      setError(true);
    }
  }, [currentSubscriptionData, selectedCurriculum]);

  return (
    <ModalContainer dismissHandler={handleDismiss}>
      <ModalHeader>
        <h3>Change Subscription</h3>
      </ModalHeader>
      <ModalContent>
        {selectedCurriculum && currentSubscriptionData && (
          <CompareCurriculumWrapper>
            <CurrentBibleStudyBrandDetails $backgroundImage={curriculumBranding?.background}>
              {curriculumBranding && (
                <CompareCurriculumImage
                  brand={{
                    background: curriculumBranding.background,
                    logo: curriculumBranding.logo as React.ReactElement,
                  }}
                />
              )}
              <h3>{currentSubscriptionData.brand?.name}</h3>
              <p>{currentSubscriptionData.ageGroup || 'Unknown'}</p>
              <p>Ministry Size: {currentSubscriptionData.subscription?.size}</p>
              <p>
                Plan: {currentSubscriptionData.subscription?.package?.toLowerCase().includes('plus') ? 'Plus' : 'Basic'}{' '}
                ({currentSubscriptionData.subscription?.period === 'year' ? 'Yearly' : 'Monthly'})
              </p>
            </CurrentBibleStudyBrandDetails>
            <ChevronIcon className="fa fa-chevron-right" />
            <BibleStudyBrandDetails $backgroundImage={selectedCurriculum?.brand?.background}>
              {selectedCurriculum.brand && <CompareCurriculumImage brand={selectedCurriculum?.brand} />}
              <h3>{selectedCurriculum.brand.name}</h3>
              <p>{selectedCurriculum.ageGroup.name}</p>
              <p>Ministry Size: {selectedSubscription?.size}</p>
              <p>
                Plan: {selectedPlan === 'plus' ? 'Plus' : 'Basic'} (
                {selectedBillingPeriod === 'year' ? 'Yearly' : 'Monthly'})
              </p>
            </BibleStudyBrandDetails>
          </CompareCurriculumWrapper>
        )}

        <SubscriptionContainer>
          <SubcriptionDetails>
            <p>{selectedBillingPeriod === 'year' ? 'Annual' : 'Monthly'} Total</p>
            <p>Beginning {format(parseISO(currentSubscriptionData?.license?.endDate || ''), 'MMM d, yyyy')}</p>
          </SubcriptionDetails>
          <SubscriptionPrice data-qa-hook="switchToAnnualPrice">
            {formatCurrency(Number(selectedSubscription?.price || 0))}
          </SubscriptionPrice>
        </SubscriptionContainer>

        <AlertBox>
          <p>Your current payment method for this subscription will be billed on your next renewal date.</p>
        </AlertBox>
        {error && <ErrorMessage>An error occurred while changing the subscription. Please try again.</ErrorMessage>}
      </ModalContent>
      <ModalFooter>
        <MediumButton onClick={handleDismiss} disabled={isSubmitting}>
          Cancel
        </MediumButton>
        <ConfirmButton
          onClick={confirm}
          disabled={isSubmitting || !currentSubscriptionData?.subscription}
          operating={isSubmitting}
        >
          Change Subscription
        </ConfirmButton>
      </ModalFooter>
    </ModalContainer>
  );
}
